import { useCallback } from 'react';

// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { useDataModelContext } from 'contexts/datamodel/useDataModel';
import { CollectionDto } from 'data/generated/graphql';
import { usePublicationSettings } from 'hooks/publication-settings';
import { AllessehContent } from 'hooks/useAllessehContentQuery';
import { agencyFromCredit, defaultImageAgency } from 'utils/article';

export default function useIsBlockedAgency() {
  const { metadata } = useDataModelContext();
  const { data } = usePublicationSettings();

  return useCallback(
    (
      contentOrCredit: AllessehContent | string | undefined,
      collectionMetadata?: CollectionDto['metadata']
    ): boolean => {
      if (!contentOrCredit || !data) {
        return false;
      }

      const agency: string | undefined =
        typeof contentOrCredit === 'string'
          ? agencyFromCredit(contentOrCredit)
          : defaultImageAgency(contentOrCredit, data.publicationSetting.defaultAltSumm);

      // Default to the publication settings
      let restricted: string[] = data.publicationSetting.restrictedAgencies;
      const allowed = data.publicationSetting.allowedAgencies;

      // Override if metadata has restrictedAgencies
      if (
        'restrictedAgencies' in metadata &&
        Array.isArray(metadata.restrictedAgencies) &&
        metadata.restrictedAgencies.length > 0
      ) {
        restricted = metadata.restrictedAgencies;
      }

      if (
        collectionMetadata &&
        'restrictedAgencies' in collectionMetadata &&
        Array.isArray(collectionMetadata.restrictedAgencies) &&
        collectionMetadata.restrictedAgencies.length > 0
      ) {
        restricted = collectionMetadata.restrictedAgencies;
      }

      if (!agency || !Array.isArray(restricted)) {
        return false;
      }

      // Empty or whitespace-only entries would compile to \b\b, matching every string — so we trim and drop them
      const sanitizedRestricted = restricted.map((s) => s.trim()).filter(Boolean);
      const sanitizedAllowed = (allowed ?? []).map((s) => s.trim()).filter(Boolean);

      // Agencies in both lists are removed from allowed — restricted takes precedence
      const effectiveAllowed = sanitizedAllowed.filter(
        (allowedAgency) => !sanitizedRestricted.some((name) => name.toLowerCase() === allowedAgency.toLowerCase())
      );

      // If credit contains at least one allowed agency, it is not blocked
      if (
        Array.isArray(effectiveAllowed) &&
        effectiveAllowed.some((allowedAgency) => {
          const escaped = allowedAgency.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          return new RegExp(`\\b${escaped}\\b`, 'i').test(agency);
        })
      ) {
        return false;
      }

      return sanitizedRestricted.some((name) => {
        const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        return new RegExp(`\\b${escaped}\\b`, 'i').test(agency);
      });
    },
    [metadata, data]
  );
}
